import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format, isSameDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, CalendarOff, CalendarX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type BlockoutDateRow = {
  id: string;
  blackout_date: string;
  reason: string | null;
};

export default function BlackoutDatesPage() {
  const navigate = useNavigate();

  // DB state (source of truth after load)
  const [savedDates, setSavedDates] = useState<BlockoutDateRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [businessId, setBusinessId] = useState<string | null>(null);

  // Staged changes
  const [datesToAdd, setDatesToAdd] = useState<
    { date: string; reason: string }[]
  >([]);
  const [dateIdsToRemove, setDateIdsToRemove] = useState<Set<string>>(
    new Set()
  );

  // Calendar / form state
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  );
  const [reason, setReason] = useState("");

  // Discard confirmation
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);

  const hasUnsavedChanges =
    datesToAdd.length > 0 || dateIdsToRemove.size > 0;

  // ---------- Load from DB ----------
  useEffect(() => {
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: bp, error: bpErr } = await supabase
          .from("business_profiles")
          .select("id")
          .eq("user_id", user.id)
          .single();
        if (bpErr) throw bpErr;

        setBusinessId(bp.id);

        const { data: rows, error: rowsErr } = await supabase
          .from("blackout_dates")
          .select("id, blackout_date, reason")
          .eq("business_id", bp.id)
          .order("blackout_date", { ascending: true });
        if (rowsErr) throw rowsErr;

        setSavedDates(rows ?? []);
      } catch (err) {
        console.error("Failed to load blackout dates:", err);
        toast.error("Failed to load blackout dates");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // ---------- Derived: effective dates for calendar display ----------
  const effectiveDates = useCallback(() => {
    const remaining = savedDates.filter(
      (d) => !dateIdsToRemove.has(d.id)
    );
    const merged = [
      ...remaining.map((d) => d.blackout_date),
      ...datesToAdd.map((d) => d.date),
    ];
    // Deduplicate
    return [...new Set(merged)];
  }, [savedDates, dateIdsToRemove, datesToAdd]);

  const effectiveDateObjects = effectiveDates().map((ds) => {
    const [y, m, d] = ds.split("-").map(Number);
    return new Date(y, m - 1, d);
  });

  // ---------- Stage a date ----------
  const handleToggleDate = () => {
    if (!selectedDate) return;
    const dateStr = format(selectedDate, "yyyy-MM-dd");

    // Is it currently in the effective list?
    const savedRow = savedDates.find((d) => d.blackout_date === dateStr);
    const stagedAdd = datesToAdd.find((d) => d.date === dateStr);

    if (savedRow && !dateIdsToRemove.has(savedRow.id)) {
      // Remove a saved date
      setDateIdsToRemove((prev) => new Set(prev).add(savedRow.id));
      setReason("");
    } else if (stagedAdd) {
      // Un-stage a pending add
      setDatesToAdd((prev) => prev.filter((d) => d.date !== dateStr));
      setReason("");
    } else if (savedRow && dateIdsToRemove.has(savedRow.id)) {
      // Re-add a removed saved date
      setDateIdsToRemove((prev) => {
        const next = new Set(prev);
        next.delete(savedRow.id);
        return next;
      });
    } else {
      // New date to add
      setDatesToAdd((prev) => [...prev, { date: dateStr, reason }]);
      setReason("");
    }
  };

  const isDateInEffective = (date: Date): boolean =>
    effectiveDateObjects.some((d) => isSameDay(d, date));

  // ---------- Save ----------
  const handleSave = async () => {
    if (!businessId) return;
    setIsSaving(true);
    try {
      // Delete removed dates
      if (dateIdsToRemove.size > 0) {
        const { error } = await supabase
          .from("blackout_dates")
          .delete()
          .eq("business_id", businessId)
          .in("id", [...dateIdsToRemove]);
        if (error) throw error;
      }

      // Insert new dates
      if (datesToAdd.length > 0) {
        const rows = datesToAdd.map((d) => ({
          business_id: businessId,
          blackout_date: d.date,
          reason: d.reason || null,
        }));
        const { error } = await supabase
          .from("blackout_dates")
          .upsert(rows, { onConflict: "business_id,blackout_date" });
        if (error) throw error;
      }

      toast.success("Blackout dates updated");
      navigate("/dashboard/calendar");
    } catch (err) {
      console.error("Failed to save blackout dates:", err);
      toast.error("Failed to save blackout dates");
    } finally {
      setIsSaving(false);
    }
  };

  // ---------- Navigation guard ----------
  const handleBack = () => {
    if (hasUnsavedChanges) {
      pendingNavigationRef.current = "/dashboard/calendar";
      setShowDiscardDialog(true);
    } else {
      navigate("/dashboard/calendar");
    }
  };

  const handleDiscard = () => {
    setShowDiscardDialog(false);
    if (pendingNavigationRef.current) {
      navigate(pendingNavigationRef.current);
    }
  };

  // ---------- Render ----------
  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="border-b bg-white sticky top-0 z-40">
        <div className="flex h-14 items-center px-4 gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <CalendarOff className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Blackout Dates</h1>
          </div>
        </div>
      </div>

      {/* Content — scrollable */}
      <div className="flex-1 overflow-y-auto pb-28 md:pb-8">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          <p className="text-sm text-muted-foreground">
            Select dates when you're unavailable for appointments. Changes are
            saved when you tap Save Changes.
          </p>

          {/* Calendar */}
          <div className="bg-white border rounded-lg p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="mx-auto"
              modifiers={{
                blocked: effectiveDateObjects,
              }}
              modifiersStyles={{
                blocked: {
                  backgroundColor: "#fee2e2",
                  color: "#ef4444",
                  fontWeight: 500,
                },
              }}
              disabled={(date) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return date < today;
              }}
            />
          </div>

          {/* Reason + toggle button */}
          <div className="bg-white border rounded-lg p-4 space-y-3">
            {selectedDate && (
              <p className="text-sm font-medium">
                {format(selectedDate, "EEEE, MMMM d, yyyy")}
                {isDateInEffective(selectedDate) && (
                  <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                    Blocked
                  </span>
                )}
              </p>
            )}

            {selectedDate && !isDateInEffective(selectedDate) && (
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Vacation, Training, etc."
                />
              </div>
            )}

            <Button
              onClick={handleToggleDate}
              disabled={!selectedDate}
              variant={
                selectedDate && isDateInEffective(selectedDate)
                  ? "outline"
                  : "default"
              }
              className="w-full"
            >
              {selectedDate && isDateInEffective(selectedDate)
                ? "Remove Date"
                : "Block This Date"}
            </Button>
          </div>

          {/* Blocked dates list */}
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium">
              Blocked Dates ({effectiveDateObjects.length})
            </h3>

            {effectiveDateObjects.length > 0 ? (
              <div className="space-y-2 max-h-[240px] overflow-y-auto">
                {effectiveDates()
                  .sort()
                  .map((dateStr) => {
                    const [y, m, d] = dateStr.split("-").map(Number);
                    const dateObj = new Date(y, m - 1, d);
                    const savedRow = savedDates.find(
                      (r) => r.blackout_date === dateStr
                    );
                    const stagedRow = datesToAdd.find(
                      (r) => r.date === dateStr
                    );
                    const reasonText =
                      stagedRow?.reason || savedRow?.reason || "";
                    const isNew = !!stagedRow;

                    return (
                      <div
                        key={dateStr}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <div>
                          <span className="text-sm">
                            {format(dateObj, "MMM dd, yyyy")}
                          </span>
                          {reasonText && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {reasonText}
                            </span>
                          )}
                          {isNew && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">
                              New
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (stagedRow) {
                              setDatesToAdd((prev) =>
                                prev.filter((dd) => dd.date !== dateStr)
                              );
                            } else if (savedRow) {
                              setDateIdsToRemove((prev) =>
                                new Set(prev).add(savedRow.id)
                              );
                            }
                          }}
                        >
                          <CalendarX className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground">
                No blackout dates set
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky bottom action bar — mobile */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t z-40 md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex gap-3 p-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleBack}
          >
            {hasUnsavedChanges ? "Cancel" : "Done"}
          </Button>
          {hasUnsavedChanges && (
            <Button
              className="flex-1 bg-figgy-500 hover:bg-figgy-600"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Desktop save bar */}
      {hasUnsavedChanges && (
        <div className="hidden md:block border-t bg-white">
          <div className="max-w-2xl mx-auto px-4 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={handleBack}>
              Cancel
            </Button>
            <Button
              className="bg-figgy-500 hover:bg-figgy-600"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Discard confirmation */}
      <AlertDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved blackout date changes. Do you want to discard
              them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
